import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import { TableData, TableRow } from "../../../../../components/Table/types.ts";
import { ClikAccountReceivable } from "../../../../../graphql/generated.ts";
import { toLocalDate } from "../../../../../utils/date.ts";
import { toCurrencyPrice } from "../../../../../utils/number.ts";

interface Props {
  data: ClikAccountReceivable[] | null | undefined;
}

export function useCRAccountReceivablesTableData({ data }: Props): TableData {
  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "providerCode",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "providerCode",
          }}
        >
          Provider
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "providerType",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "providerType",
          }}
        >
          Provider Type
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "transactionType",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "transactionType",
          }}
        >
          Transaction Type
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "transactionStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "transactionStatus",
          }}
        >
          Transaction Status
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "transactionAmount",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "transactionAmount",
          }}
        >
          Transaction Amount
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "documentDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "documentDate",
          }}
        >
          Document Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "expiryDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "expiryDate",
          }}
        >
          Expiry/Due Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "closingDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "closingDate",
          }}
        >
          Closing Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "disputeFlag",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "disputeFlag",
          }}
        >
          Dispute Flag
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "disputeDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "disputeDate",
          }}
        >
          Dispute Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "referenceDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "ClikAccountReceivable",
            fieldName: "referenceDate",
          }}
        >
          Last Reference Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
  ];

  const rows = useMemo(() => {
    return data?.map((item) => ({
      providerCode: item.providerCode,
      providerType: item.providerType,
      transactionType: item.transactionType,
      transactionStatus: item.transactionStatus,
      transactionAmount:
        item.transactionAmount && toCurrencyPrice(item.transactionAmount),
      documentDate: item.documentDate && toLocalDate(item.documentDate),
      expiryDate: item.expiryDate && toLocalDate(item.expiryDate),
      closingDate: item.closingDate && toLocalDate(item.closingDate),
      disputeFlag: item.disputeFlag,
      disputeDate: item.disputeDate && toLocalDate(item.disputeDate),
      referenceDate: item.referenceDate && toLocalDate(item.referenceDate),
    }));
  }, [data]);

  return [columns, rows];
}
